/**
 * Restaurant-wide settings live on the restaurant document under
 * `settings.<block>` (docs/SEEDING_DEFAULTS.md, "Settings blocks"). This module
 * is the generic half every block shares, on both planes (index + node):
 *
 * - `readSettingsBlock` — the block, or `null` while it is absent (absent =
 *   every key at its default).
 * - `sparseSettingsBlock` — only the keys that differ from the block's defaults.
 * - `planSettingsBlockWrite` / `settingsBlockUpdateFields` — the per-key dotted
 *   write that moves a stored block to a new one: changed keys set, keys back at
 *   their default deleted, the whole block written only when it is created.
 * - `settingsMapFieldWrites` — how a writer that submits a WHOLE `settings` map
 *   (the site-admin RestaurantModal, the API restaurant PUT) is written: key by
 *   key, never touching a key it does not name, never touching a key with a
 *   dedicated writer (`RESTAURANT_SETTINGS_KEYS_WITH_DEDICATED_WRITER`).
 */
import { LOCATION_SETTINGS_KEYS_WITH_DEDICATED_WRITER } from '@order-up/models';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Order-insensitive structural equality for the JSON-shaped values stored here. */
export function settingsValueEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => settingsValueEqual(v, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ak = Object.keys(a).filter((k) => a[k] !== undefined);
    const bk = Object.keys(b).filter((k) => b[k] !== undefined);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => settingsValueEqual(a[k], b[k]));
  }
  return false;
}

/**
 * `settings.<block>` from a raw restaurant document, or `null` when absent. A
 * non-object value counts as absent rather than as an empty authoritative block.
 */
export function readSettingsBlock(
  locationData: unknown,
  block: string,
): Record<string, unknown> | null {
  if (!isPlainObject(locationData)) return null;
  const settings = locationData.settings;
  if (!isPlainObject(settings)) return null;
  const value = settings[block];
  return isPlainObject(value) ? value : null;
}

export interface SparseBlockSpec<K extends string> {
  /** Every key the block may carry — membership is the contract. */
  readonly keys: readonly K[];
  /** What an absent key means. */
  readonly defaults: Readonly<Record<K, unknown>>;
  /** Canonicalise a stored value before comparing (e.g. '' guid → null). */
  readonly normalize?: (key: K, value: unknown) => unknown;
  /** Extra "is this the default" rule (e.g. an empty map or list). */
  readonly isDefault?: (key: K, value: unknown) => boolean;
}

/** Only the contract keys whose value is present and differs from the default. */
export function sparseSettingsBlock<K extends string>(
  spec: SparseBlockSpec<K>,
  source: Readonly<Record<string, unknown>> | null | undefined,
): Partial<Record<K, unknown>> {
  const out: Partial<Record<K, unknown>> = {};
  if (!source) return out;
  for (const key of spec.keys) {
    if (!(key in source) || source[key] === undefined) continue;
    const value = spec.normalize ? spec.normalize(key, source[key]) : source[key];
    if (spec.isDefault?.(key, value)) continue;
    if (settingsValueEqual(value, spec.defaults[key])) continue;
    out[key] = value;
  }
  return out;
}

/** Every contract key, with the default wherever the sparse block is silent. */
export function resolveSettingsBlock<K extends string>(
  spec: SparseBlockSpec<K>,
  block: Readonly<Record<string, unknown>> | null | undefined,
): Record<K, unknown> {
  return { ...spec.defaults, ...sparseSettingsBlock(spec, block) } as Record<K, unknown>;
}

export interface SettingsBlockWritePlan<K extends string = string> {
  /** Non-null only when the block does not exist yet: create it whole. */
  readonly replaceBlock: Partial<Record<K, unknown>> | null;
  readonly set: Partial<Record<K, unknown>>;
  readonly remove: readonly K[];
}

/** What to write to move the stored block to `next` (both sparse). */
export function planSettingsBlockWrite<K extends string>(
  keys: readonly K[],
  stored: Readonly<Record<string, unknown>> | null,
  next: Readonly<Record<string, unknown>>,
): SettingsBlockWritePlan<K> {
  if (stored === null) return { replaceBlock: { ...next } as Partial<Record<K, unknown>>, set: {}, remove: [] };
  const set: Partial<Record<K, unknown>> = {};
  const remove: K[] = [];
  for (const key of keys) {
    const has = key in next && next[key] !== undefined;
    const had = key in stored && stored[key] !== undefined;
    if (has && (!had || !settingsValueEqual(next[key], stored[key]))) set[key] = next[key];
    if (!has && had) remove.push(key);
  }
  return { replaceBlock: null, set, remove };
}

export function isEmptySettingsBlockWritePlan(plan: SettingsBlockWritePlan): boolean {
  return plan.replaceBlock === null && Object.keys(plan.set).length === 0 && plan.remove.length === 0;
}

/**
 * The plan as a Firestore `update()` map on the RESTAURANT document. The delete
 * sentinel is passed in: the client SDK's `deleteField()` and the Admin SDK's
 * `FieldValue.delete()` are different objects.
 */
export function settingsBlockUpdateFields(
  block: string,
  plan: SettingsBlockWritePlan,
  deleteSentinel: unknown,
): Record<string, unknown> {
  const path = `settings.${block}`;
  if (plan.replaceBlock !== null) return { [path]: { ...plan.replaceBlock } };
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(plan.set)) fields[`${path}.${key}`] = value;
  for (const key of plan.remove) fields[`${path}.${key}`] = deleteSentinel;
  return fields;
}

/** A `settings` map with every dedicated-writer key removed. */
export function withoutDedicatedSettingsKeys(
  settings: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const dedicated = new Set<string>(LOCATION_SETTINGS_KEYS_WITH_DEDICATED_WRITER);
  return Object.fromEntries(Object.entries(settings).filter(([key]) => !dedicated.has(key)));
}

/**
 * A whole `settings` map as per-key writes.
 *
 * `named` is the map the writer submitted (dedicated keys already removed by
 * `withoutDedicatedSettingsKeys`); `validated` is the same map after schema
 * parsing and value conversion. A key the writer NAMED is set to its validated
 * value, or deleted when it named it with no value (how a form clears a time
 * field). A key it did not name is not written at all — so no whole-map writer
 * can delete another screen's setting, which is what the old map replace did.
 */
export function settingsMapFieldWrites(
  named: Readonly<Record<string, unknown>>,
  validated: Readonly<Record<string, unknown>> | undefined,
  deleteSentinel: unknown,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  for (const key of Object.keys(named)) {
    const value = validated?.[key];
    fields[`settings.${key}`] = value === undefined ? deleteSentinel : value;
  }
  return fields;
}
